import CryptoJS from 'crypto-js'

// 签名密钥
const SECRET_KEY = 'dX7k9mP2qR8vY4nW1zL5tA3sE6jH0cB8'

/**
 * 生成当前时间戳（秒）
 */
export const getCurrentTimestamp = (): number => {
  return Math.floor(Date.now() / 1000)
}

/**
 * 生成随机 nonce
 * @param length - nonce 长度，默认 16
 */
export const generateNonce = (length: number = 16): string => {
  const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'
  const random = Date.now()
  let result = ''
  
  for (let i = 0; i < length; i++) {
    result += chars[(random + i) % chars.length]
  }
  
  return result + random.toString()
}

/**
 * 构建签名参数（过滤空值并添加时间戳和随机数）
 * @param params - 请求参数
 * @param timestamp - 时间戳
 * @param nonce - 随机字符串
 */
const buildSignParams = (
  params: Record<string, any>,
  timestamp: number,
  nonce: string
): Record<string, string> => {
  const signParams: Record<string, string> = {}
  
  // 过滤空值和 sign 参数
  Object.keys(params).forEach((key) => {
    const value = params[key]
    if (value !== null && value !== undefined && value !== '' && key !== 'sign') {
      signParams[key] = String(value)
    }
  })
  
  // 添加时间戳和随机数
  signParams['timestamp'] = timestamp.toString()
  signParams['nonce'] = nonce
  
  return signParams
}

/**
 * 构建待签名字符串
 * @param signParams - 签名参数
 */
const buildSignString = (signParams: Record<string, string>): string => {
  // 按参数名排序
  const sortedKeys = Object.keys(signParams).sort()
  
  // 拼接参数字符串
  const paramPairs = sortedKeys.map((key) => {
    const encodedKey = encodeURIComponent(key)
    const encodedValue = encodeURIComponent(signParams[key])
    return `${encodedKey}=${encodedValue}`
  })
  
  const paramString = paramPairs.join('&')
  
  // 追加密钥
  return paramString + SECRET_KEY
}

/**
 * 生成签名
 * @param params - 请求参数（URL 查询参数 + JSON body 参数）
 * @param timestamp - 时间戳（秒）
 * @param nonce - 随机字符串
 * @returns HMAC-SHA256 签名的十六进制字符串
 */
export const generateSignature = (
  params: Record<string, any>,
  timestamp: number,
  nonce: string
): string => {
  // 1. 构建签名参数（过滤空值并添加时间戳和随机数）
  const signParams = buildSignParams(params, timestamp, nonce)
  
  // 2. 生成签名字符串
  const signStr = buildSignString(signParams)
  
  // 3. 计算 HMAC-SHA256
  const hmac = CryptoJS.HmacSHA256(signStr, SECRET_KEY)
  
  // 4. 返回十六进制字符串
  return hmac.toString(CryptoJS.enc.Hex)
}

/**
 * 生成请求签名头部
 * @param params - 请求参数
 */
export const generateSignatureHeaders = (params: Record<string, any> = {}) => {
  const timestamp = getCurrentTimestamp()
  const nonce = generateNonce()
  const signature = generateSignature(params, timestamp, nonce)
  
  return {
    'X-Timestamp': timestamp.toString(),
    'X-Nonce': nonce,
    'X-Sign': signature
  }
}
